
import React, { useState, useEffect } from 'react';
import { Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MainLayout } from '@/components/MainLayout';
import { AppLoading } from './AppLoading';
import { ErrorBoundary } from './ErrorBoundary';
import { toast } from '@/components/ui/sonner';
import { AuthPaymentProvider } from '@/contexts/AuthPaymentContext';

import { PublicRoutes } from './PublicRoutes';
import { AdminRoutes } from './AdminRoutes';
import { CourierRoutes } from './CourierRoutes';
import PaymentAuthComplete from '@/pages/payment/PaymentAuthComplete';
import PaymentAuthStatus from '@/pages/payment/PaymentAuthStatus';
import Index from '@/pages/Index';
import NotFound from '@/pages/NotFound';
import PaymentAuthSuccess from '@/pages/payment/PaymentAuthSuccess';

/**
 * Основной компонент маршрутизации приложения
 */
export default function AppRoutes() {
  const { user, isAdmin, isLoading } = useAuth();
  const location = useLocation();
  const [routeError, setRouteError] = useState<Error | null>(null);

  // Эффект для сброса ошибок при смене маршрута
  useEffect(() => {
    setRouteError(null);
  }, [location.pathname]);

  // Обработчик ошибок при рендеринге маршрутов
  const handleRouteError = (error: Error) => {
    console.error('Ошибка при отображении маршрута:', error);
    setRouteError(error);
    toast.error('Произошла ошибка при загрузке страницы');
  };

  // Отображаем индикатор загрузки, пока проверяется аутентификация
  if (isLoading) {
    return <AppLoading />;
  }

  // Отображаем компонент ошибки, если возникла ошибка при рендеринге
  if (routeError) {
    return <ErrorBoundary error={routeError} resetError={() => setRouteError(null)} />;
  }

  return (
    <React.Suspense fallback={<AppLoading />}>
      <Routes>
        {/* Публичные маршруты */}
        <Route path="/login" element={<PublicRoutes />} />
        <Route path="/payment/auth/success" element={<PaymentAuthSuccess />} />

        {/* Защищенные маршруты с общим макетом */}
        <Route element={
          <AuthPaymentProvider>
            <MainLayout children={<Outlet />} />
          </AuthPaymentProvider>
        }>
          {/* Публичный корневой маршрут внутри макета */}
          <Route path="/" element={<Index />} />
          
          {/* Маршрут для завершения авторизации платежа */}
          <Route path="/payment/auth/complete" element={<PaymentAuthComplete />} />
          
          {/* Новый маршрут для проверки статуса авторизации платежа */}
          <Route path="/payment/auth/status" element={<PaymentAuthStatus />} />

          {/* Маршруты администратора */}
          {AdminRoutes({ isAdmin })}
          
          {/* Маршруты курьера */}
          {CourierRoutes({ isAuthenticated: !!user })}
          
          {/* Маршрут для страницы 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </React.Suspense>
  );
}
